// src/components/NotificationPanel.tsx
import { Button } from "@/components/ui/button";
import { Megaphone, Bell as BellIcon, ChevronRight, Clock } from "lucide-react";
import { useNavigate } from "react-router";
import { Notification, notificationKey } from "@/api/service/notifications";
import { cn } from "@/lib/utils";

// A compact preview that hides walls-of-body-text behind a tap. Title-first
// for announcements; first-line summary for personal notifications.
function previewLine(n: Notification): string {
  if (n.kind === "announcement" && n.title) return n.title;
  const flat = n.message.replace(/\s+/g, " ").trim();
  return flat;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

export default function NotificationPanel({
  notifications,
  loading,
  error,
  clearing,
  clearAll,
  onOpenDetail,
}: {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  clearing: boolean;
  clearAll: () => void;
  /** Called when a notification needs a detail view (announcements always;
   *  personal notifications without a path). */
  onOpenDetail: (n: Notification) => void;
}) {
  const navigate = useNavigate();

  const handleClick = (n: Notification) => {
    // Personal notifications with a path keep their original "go-to" behavior
    // (the link IS the action). Everything else opens the detail modal.
    if (n.kind === "personal" && n.path) {
      navigate(n.path);
      return;
    }
    onOpenDetail(n);
  };

  return (
    <div className="w-[340px] p-2">
      <div className="flex justify-between items-center mb-2 px-2 pt-1">
        <h2 className="text-sm font-semibold text-foreground">Notifications</h2>
        {notifications.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground h-7 px-2"
            onClick={clearAll}
            disabled={clearing}
          >
            {clearing ? "Clearing…" : "Clear all"}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-xs text-muted-foreground">
          Loading…
        </div>
      ) : error ? (
        <div className="py-10 text-center text-xs text-destructive">{error}</div>
      ) : notifications.length === 0 ? (
        <div className="py-10 text-center text-xs text-muted-foreground">
          You're all caught up.
        </div>
      ) : (
        <ul className="space-y-0.5 max-h-[60vh] overflow-y-auto pr-0.5">
          {notifications.map((n) => (
            <li key={notificationKey(n)}>
              <button
                type="button"
                onClick={() => handleClick(n)}
                className={cn(
                  "w-full text-left flex items-start gap-2.5 px-2.5 py-2 rounded-md",
                  "hover:bg-accent transition-colors group"
                )}
              >
                <span
                  className={cn(
                    "shrink-0 mt-0.5 inline-flex items-center justify-center h-6 w-6 rounded-md",
                    n.kind === "announcement"
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                  )}
                  aria-hidden
                >
                  {n.kind === "announcement" ? (
                    <Megaphone className="h-3.5 w-3.5" />
                  ) : (
                    <BellIcon className="h-3.5 w-3.5" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-foreground line-clamp-1 leading-snug">
                    {previewLine(n)}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Clock className="h-2.5 w-2.5" />
                    <span>{formatTime(n.created_at)}</span>
                    {n.kind === "announcement" && (
                      <>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="font-medium text-primary/80">
                          announcement
                        </span>
                      </>
                    )}
                  </span>
                </span>
                <ChevronRight className="shrink-0 h-3.5 w-3.5 text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity self-center" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
